import React, { useState } from 'react';
import { usePageTitle } from '../components/AppShell';
import { useLang } from '../lib/lang';
import { type AuthUser } from '../lib/auth';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }

interface Message {
  id:        string;
  from:      string;
  fromEn:    string;
  direction: 'school' | 'parent';
  subject:   string;
  subjectEn: string;
  preview:   string;
  previewEn: string;
  ts:        string;
  read:      boolean;
  tag:       'kehadiran' | 'akademik' | 'am';
  fullMs:    string;
  fullEn:    string;
}

const MESSAGES: Message[] = [
  {
    id:'m1', direction:'school',
    from:'Cikgu Ahmad Zulkarnain (Guru Matematik)', fromEn:'Cikgu Ahmad Zulkarnain (Mathematics Teacher)',
    subject:'Prestasi Matematik Ahmad — Penggal 2', subjectEn:'Ahmad\'s Mathematics Performance — Term 2',
    preview:'Prestasi Ahmad dalam Matematik menunjukkan peningkatan yang baik...', previewEn:'Ahmad\'s performance in Mathematics shows good improvement...',
    ts:'10:32 pagi, 2 Jun 2025', read:false, tag:'akademik',
    fullMs:`Assalamualaikum,

Saya ingin memaklumkan bahawa Ahmad Zulfikri menunjukkan peningkatan yang sangat membanggakan dalam mata pelajaran Matematik bagi Penggal 2 ini.

Pada peperiksaan pertengahan penggal, Ahmad mendapat markah 91%, meningkat daripada 88% pada penggal lepas. Beliau semakin yakin dalam tajuk algebra dan geometri.

Saya menggalakkan Ahmad untuk terus berlatih soalan tambahan dan tidak teragak-agak untuk bertanya jika ada perkara yang kurang jelas.

Terima kasih atas sokongan ibu bapa yang berterusan.

Hormat saya,
Cikgu Ahmad Zulkarnain`,
    fullEn:`Assalamualaikum,

I am pleased to inform you that Ahmad Zulfikri has shown impressive improvement in Mathematics for this Term 2.

In the mid-term examination, Ahmad scored 91%, up from 88% last term. He is increasingly confident in algebra and geometry topics.

I encourage Ahmad to continue practising additional questions and not hesitate to ask if anything is unclear.

Thank you for your continued parental support.

Regards,
Cikgu Ahmad Zulkarnain`,
  },
  {
    id:'m2', direction:'school',
    from:'Pejabat Am, SMAS Sayyidina Ali', fromEn:'General Office, SMAS Sayyidina Ali',
    subject:'Makluman Ketidakhadiran — 10 Jun 2025', subjectEn:'Absence Notification — 10 June 2025',
    preview:'Ini adalah makluman automatik bahawa Ahmad Zulfikri tidak hadir...', previewEn:'This is an automatic notification that Ahmad Zulfikri was absent...',
    ts:'8:45 pagi, 10 Jun 2025', read:true, tag:'kehadiran',
    fullMs:`Ibu Bapa / Penjaga yang dihormati,

Ini adalah makluman automatik bahawa Ahmad Zulfikri (Tingkatan 4A) TIDAK HADIR ke sekolah pada 10 Jun 2025.

Jika ketidakhadiran ini sah (sakit, kecemasan keluarga, dll.), sila kemukakan surat MC atau surat ibu bapa kepada Warden Kelas dalam tempoh 2 hari persekolahan.

Jika anda mempunyai sebarang pertanyaan, sila hubungi pejabat sekolah di +673-2229500.

Terima kasih.`,
    fullEn:`Dear Parent / Guardian,

This is an automatic notification that Ahmad Zulfikri (Form 4A) was ABSENT from school on 10 June 2025.

If this absence is valid (illness, family emergency, etc.), please submit an MC or parent letter to the Class Warden within 2 school days.

If you have any enquiries, please contact the school office at +673-2229500.

Thank you.`,
  },
  {
    id:'m3', direction:'school',
    from:'Cikgu Aishah Hj Salleh (Guru Kelas 4A)', fromEn:'Cikgu Aishah Hj Salleh (Class Teacher 4A)',
    subject:'Perjumpaan Ibu Bapa & Guru — Julai 2025', subjectEn:'Parent-Teacher Meeting — July 2025',
    preview:'Dimaklumkan bahawa Perjumpaan Ibu Bapa & Guru akan diadakan...', previewEn:'Please be informed that the Parent-Teacher Meeting will be held...',
    ts:'2:15 ptg, 28 Mei 2025', read:true, tag:'am',
    fullMs:`Ibu Bapa / Penjaga yang dihormati,

Dimaklumkan bahawa Perjumpaan Ibu Bapa & Guru bagi Penggal 2 2025 akan diadakan pada:

Tarikh: 18 Julai 2025 (Jumaat)
Masa: 8:00 pagi – 12:00 tengahari
Tempat: Dewan Sekolah SMAS Sayyidina Ali

Sila pastikan kehadiran anda pada tarikh tersebut.

Bagi ibu bapa yang tidak dapat hadir, sila maklumkan kepada pihak sekolah terlebih dahulu.

Terima kasih.`,
    fullEn:`Dear Parent / Guardian,

Please be informed that the Parent-Teacher Meeting for Term 2 2025 will be held on:

Date: 18 July 2025 (Friday)
Time: 8:00 AM – 12:00 PM
Venue: School Hall, SMAS Sayyidina Ali

Please ensure your attendance on that date.

Parents who are unable to attend should inform the school in advance.

Thank you.`,
  },
];

const FILTER_OPTS = [
  { key:'all',       ms:'Semua',              en:'All'              },
  { key:'kehadiran', ms:'Mengenai Kehadiran', en:'Attendance'       },
  { key:'akademik',  ms:'Mengenai Akademik',  en:'Academic'         },
  { key:'am',        ms:'Umum',              en:'General'          },
];

export function ParentKomunikasiPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'Communication' : 'Komunikasi');

  const [filter, setFilter]   = useState('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const shown = MESSAGES.filter(m => filter === 'all' || m.tag === filter);

  // Group by date label
  const groups: { label: string; msgs: Message[] }[] = [];
  const seenDates = new Set<string>();
  for (const m of shown) {
    const dateLabel = m.ts.split(',')[1]?.trim() ?? m.ts;
    if (!seenDates.has(dateLabel)) {
      seenDates.add(dateLabel);
      groups.push({ label: dateLabel, msgs: [] });
    }
    groups.at(-1)!.msgs.push(m);
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      <div className="page-header">
        <div>
          <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
            {E ? 'Communication' : 'Komunikasi'}
          </h1>
          <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
            {E ? 'Message history between school and family' : 'Sejarah komunikasi antara sekolah dan keluarga'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {FILTER_OPTS.map(f => (
          <button key={f.key} className={`pill-filter${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>
            {E ? f.en : f.ms}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {shown.map(m => {
          const isExp = expanded === m.id;
          const borderColor = m.direction === 'school' ? '#1D4ED8' : '#F7D000';
          const tagColors: Record<string, { bg: string; color: string }> = {
            kehadiran: { bg: T.color.dangerLight,   color: T.color.danger   },
            akademik:  { bg: T.color.infoLight,     color: T.color.info     },
            am:        { bg: '#F3F4F6',              color: '#4A5568'        },
          };
          const tc = tagColors[m.tag] ?? tagColors.am!;

          return (
            <div
              key={m.id}
              style={{
                background: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderLeft: `4px solid ${borderColor}`,
                borderRadius: '10px',
                padding: '18px 20px',
                cursor: 'pointer',
                transition: 'box-shadow 150ms ease',
                boxShadow: isExp ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.06)',
              }}
              onClick={() => setExpanded(isExp ? null : m.id)}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                {!m.read && (
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#1D4ED8', flexShrink: 0, marginTop: '6px' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: T.color.textPrimary }}>
                      {E ? m.fromEn : m.from}
                    </span>
                    <span className="badge" style={{ background: tc.bg, color: tc.color, fontSize: '10px' }}>
                      {E ? FILTER_OPTS.find(f => f.key === m.tag)?.en : FILTER_OPTS.find(f => f.key === m.tag)?.ms}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: T.color.textPrimary, marginBottom: '4px' }}>
                    {E ? m.subjectEn : m.subject}
                  </div>
                  {!isExp && (
                    <div style={{ fontSize: '12px', color: T.color.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {E ? m.previewEn : m.preview}
                    </div>
                  )}
                  {isExp && (
                    <div style={{ marginTop: '12px', padding: '14px', background: '#F9FAFB', borderRadius: '6px', fontSize: '13px', color: T.color.textPrimary, lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                      {E ? m.fullEn : m.fullMs}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: T.color.textDisabled, marginTop: '8px' }}>
                    {m.ts}
                    {m.read
                      ? <span style={{ marginLeft: '8px', color: T.color.success }}>✓✓ {E?'Read':'Dibaca'}</span>
                      : <span style={{ marginLeft: '8px', color: '#9CA3AF' }}>✓ {E?'Delivered':'Dihantar'}</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {shown.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: T.color.textSecondary, fontSize: '13px' }}>
            {E ? 'No messages for this filter.' : 'Tiada mesej untuk penapis ini.'}
          </div>
        )}
      </div>
    </div>
  );
}
